import { Users, Lock, Zap } from "lucide-react";

const FEATURES = [
    {
        id: "community",
        title: "Community-Driven",
        desc: "Decentralized ownership. No single point of failure. The mission belongs to the holders — every voice steers the rocket.",
        icon: Users,
        accent: "#00F0FF",
        tag: "01 / Power",
    },
    {
        id: "fair-launch",
        title: "Fair Launch",
        desc: "Zero pre-sale. Zero team tokens. Zero gimmicks. A clean slate where every astronaut starts at the launchpad together.",
        icon: Lock,
        accent: "#B026FF",
        tag: "02 / Integrity",
    },
    {
        id: "space-speed",
        title: "Space-Speed",
        desc: "Built on Solana's hypersonic architecture. Sub-second confirmations. Microscopic fees. Pure thrust, no drag.",
        icon: Zap,
        accent: "#00F0FF",
        tag: "03 / Velocity",
    },
];

export default function Features() {
    return (
        <section
            id="features"
            data-testid="features-section"
            className="relative py-28 sm:py-36 px-5 sm:px-8 overflow-hidden"
        >
            {/* Background */}
            <div
                aria-hidden
                className="absolute inset-0 grid-texture opacity-30 [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_80%)]"
            />
            <div
                aria-hidden
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[#B026FF]/10 blur-[180px]"
            />

            <div className="relative z-10 max-w-7xl mx-auto">
                {/* Section header */}
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16 gap-6">
                    <div className="max-w-2xl">
                        <div className="font-mono text-xs tracking-[0.3em] uppercase text-[#00F0FF] mb-4">
                            // FLIGHT PROTOCOL
                        </div>
                        <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white">
                            Built for the
                            <br />
                            <span className="gradient-text">long-haul orbit</span>
                        </h2>
                    </div>
                    <p className="max-w-md font-mono text-sm text-slate-400 leading-relaxed">
                        Three principles fuel SPACE_100X. No compromises, no
                        shortcuts — only the systems that get us to lunar
                        velocity.
                    </p>
                </div>

                {/* Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
                    {FEATURES.map((f, idx) => {
                        const Icon = f.icon;
                        return (
                            <article
                                key={f.id}
                                data-testid={`feature-card-${f.id}`}
                                className={`group card-hover relative bg-[#0B0B1A] border border-white/10 rounded-2xl p-8 sm:p-10 overflow-hidden ${
                                    idx === 1 ? "md:translate-y-8" : ""
                                }`}
                            >
                                {/* Corner accent */}
                                <div
                                    aria-hidden
                                    className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl opacity-50 group-hover:opacity-80 transition-opacity"
                                    style={{ background: f.accent }}
                                />
                                <div
                                    aria-hidden
                                    className="absolute top-0 left-0 h-px w-16 group-hover:w-full transition-all duration-500"
                                    style={{ background: f.accent }}
                                />

                                <div className="relative">
                                    {/* Tag */}
                                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-8">
                                        {f.tag}
                                    </div>

                                    {/* Icon */}
                                    <div
                                        className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 border"
                                        style={{
                                            background: `${f.accent}15`,
                                            borderColor: `${f.accent}55`,
                                            boxShadow: `inset 0 0 18px ${f.accent}22, 0 0 24px ${f.accent}22`,
                                        }}
                                    >
                                        <Icon
                                            size={24}
                                            style={{ color: f.accent }}
                                        />
                                    </div>

                                    {/* Title */}
                                    <h3
                                        className="font-display text-2xl sm:text-3xl font-black tracking-tight text-white mb-4"
                                    >
                                        {f.title}
                                    </h3>

                                    {/* Description */}
                                    <p className="font-mono text-sm text-slate-400 leading-relaxed">
                                        {f.desc}
                                    </p>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
